import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Package, Wrench, AlertTriangle, ShieldCheck, RefreshCw } from 'lucide-react';
import HireCosts from './HireCosts';

const StatCard = ({ title, testId, value, icon: Icon, description, variant }) => (
  <Card data-testid={`stat-card-${testId}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <Icon className={`w-4 h-4 ${variant === 'warning' ? 'text-amber-500' : variant === 'danger' ? 'text-red-500' : 'text-muted-foreground'}`} />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    </CardContent>
  </Card>
);

const InventoryDashboard = ({ dashboard, onRefresh }) => {
  const { t } = useTranslation();

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {t('inv.dashboard.loading')}
      </div>
    );
  }

  const { total_assets, by_type, by_category, by_condition, upcoming_inspections, expiring_warranties, total_value_by_currency, by_ownership, returns_due } = dashboard;

  const valueStr = Object.entries(total_value_by_currency || {})
    .map(([cur, val]) => `${val.toLocaleString()} ${cur}`)
    .join(', ') || t('common.none');

  const own = by_ownership || {};
  const returns = returns_due || {};

  return (
    <div className="space-y-6" data-testid="inventory-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('inv.dashboard.title')}</h2>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-dashboard-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> {t('common.refresh')}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="total-assets" title={t('inv.dashboard.totalAssets')} value={total_assets} icon={Package}
          description={t('inv.dashboard.typeSplit', { tangible: by_type?.tangible || 0, intangible: by_type?.intangible || 0 })} />
        <StatCard testId="total-value" title={t('inv.dashboard.totalValue')} value={valueStr} icon={Package} />
        <StatCard testId="inspections-due" title={t('inv.dashboard.inspectionsDue')} value={upcoming_inspections}
          icon={AlertTriangle} description={t('inv.dashboard.within30')}
          variant={upcoming_inspections > 0 ? 'warning' : undefined} />
        <StatCard testId="warranties-expiring" title={t('inv.dashboard.warrantiesExpiring')} value={expiring_warranties}
          icon={ShieldCheck} description={t('inv.dashboard.within60')}
          variant={expiring_warranties > 0 ? 'danger' : undefined} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="our-own-kit" title={t('inv.dashboard.ownKit')} value={own.own ?? 0} icon={Package}
          description={t('inv.dashboard.ownKitHint')} />
        <StatCard testId="customer-provided" title={t('inv.ownership.customer_provided.label')}
          value={own.customer_provided ?? 0} icon={Wrench} description={t('inv.dashboard.customerKitHint')} />
        <StatCard testId="rented-in" title={t('inv.dashboard.rentedIn')} value={own.rented ?? 0} icon={Wrench}
          description={t('inv.dashboard.rentedInHint')} />
        <StatCard testId="returns-overdue" title={t('inv.dashboard.returnsOverdue')} value={returns.overdue ?? 0}
          icon={AlertTriangle}
          description={t('inv.dashboard.dueSoonCount', { count: returns.due_soon ?? 0 })}
          variant={(returns.overdue ?? 0) > 0 ? 'danger' : (returns.due_soon ?? 0) > 0 ? 'warning' : undefined} />
      </div>

      <HireCosts />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm font-medium">{t('inv.dashboard.byCategory')}</CardTitle></CardHeader>
          <CardContent>
            {(by_category || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('inv.dashboard.noAssetsYet')}</p>
            ) : (
              <div className="space-y-2">
                {by_category.map((item) => (
                  <div key={item.category} className="flex items-center justify-between">
                    <span className="text-sm">{item.category}</span>
                    <Badge variant="secondary">{item.count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm font-medium">{t('inv.dashboard.byCondition')}</CardTitle></CardHeader>
          <CardContent>
            {(by_condition || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('inv.dashboard.noAssetsYet')}</p>
            ) : (
              <div className="space-y-2">
                {by_condition.map((item) => (
                  <div key={item.condition} className="flex items-center justify-between">
                    <span className="text-sm">{item.condition}</span>
                    <Badge variant={item.condition === 'Scrap' ? 'destructive' : item.condition === 'Needs Repair' ? 'outline' : 'secondary'}>
                      {item.count}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InventoryDashboard;
